import { useCallback, useEffect, useRef, useState } from 'react'

const MAX_SLIDER = 21
const INITIAL_PERCENT = 1

const getProfessionals = (percent: number) => Math.floor((percent * MAX_SLIDER) / 100 + 2)

type CotizatorSliderProps = {
  onChange: (professionals: number) => void
  onCommit: (professionals: number) => void
}

export function CotizatorSlider({ onChange, onCommit }: CotizatorSliderProps) {
  const sliderRef = useRef<HTMLDivElement>(null)
  const [percent, setPercent] = useState(INITIAL_PERCENT)
  const professionalsRef = useRef(getProfessionals(INITIAL_PERCENT))
  const dragStartProfessionalsRef = useRef<number | null>(null)

  const moveTo = useCallback(
    (pageX: number) => {
      const slider = sliderRef.current
      if (!slider) {
        return
      }
      const rect = slider.getBoundingClientRect()
      const width = rect.width
      const offset = rect.left + window.scrollX
      const newWidth = pageX - offset
      if (newWidth < 0 || newWidth > width) {
        return
      }
      const value = Math.round((newWidth / width) * 100)
      setPercent(value)
      const professionals = getProfessionals(value)
      if (professionals === professionalsRef.current) {
        return
      }
      professionalsRef.current = professionals
      onChange(professionals)
    },
    [onChange],
  )

  const startDrag = (pageX: number) => {
    dragStartProfessionalsRef.current = professionalsRef.current
    moveTo(pageX)
  }

  const endDrag = useCallback(() => {
    const startProfessionals = dragStartProfessionalsRef.current
    if (startProfessionals === null) {
      return
    }
    dragStartProfessionalsRef.current = null
    if (startProfessionals !== professionalsRef.current) {
      onCommit(professionalsRef.current)
    }
  }, [onCommit])

  useEffect(() => {
    const isDragging = () => dragStartProfessionalsRef.current !== null
    const handleMove = (event: MouseEvent) => {
      if (isDragging()) {
        moveTo(event.pageX)
      }
    }
    const handleTouch = (event: TouchEvent) => {
      const touch = event.touches[0]
      if (isDragging() && touch) {
        moveTo(touch.pageX)
      }
    }
    const slider = sliderRef.current
    document.addEventListener('mousemove', handleMove)
    document.addEventListener('mouseup', endDrag)
    slider?.addEventListener('touchmove', handleTouch)
    slider?.addEventListener('touchend', endDrag)
    slider?.addEventListener('touchcancel', endDrag)
    return () => {
      document.removeEventListener('mousemove', handleMove)
      document.removeEventListener('mouseup', endDrag)
      slider?.removeEventListener('touchmove', handleTouch)
      slider?.removeEventListener('touchend', endDrag)
      slider?.removeEventListener('touchcancel', endDrag)
    }
  }, [moveTo, endDrag])

  return (
    <div className="rkmd-slider slider-discrete slider-lightBlue slider-light">
      <input type="range" min={1} max={100} value={percent} readOnly />
      <div
        ref={sliderRef}
        className="slider"
        onMouseDown={(event) => {
          if (event.button !== 2) {
            startDrag(event.pageX)
          }
        }}
        onTouchStart={(event) => {
          const touch = event.touches[0]
          if (touch) {
            startDrag(touch.pageX)
          }
        }}
      >
        <div className="slider-fill" style={{ width: `${percent}%` }} />
        <div className="slider-handle" style={{ left: `${percent}%`, transition: 'none' }}>
          <div className="slider-label">
            <span>{percent}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
